import type { ChartConfig, StyledCurveReference } from '@/interfaces/waveChart'
import { WaveChartDefaults } from '@/interfaces/waveChart'

function normalizeColor(color?: string): string | undefined {
  if (!color) return undefined
  return color.trim().toLowerCase()
}

/**
 * Returns the list of colors already used by curves in a chart (normalized).
 */
export function getUsedColors(chart: ChartConfig): string[] {
  const used = new Set<string>()
  chart.curveReferences.forEach((ref: StyledCurveReference) => {
    const c = normalizeColor(ref.styling?.color)
    if (c) used.add(c)
  })
  return Array.from(used)
}

/**
 * Picks the next color for a chart from the provided palette.
 * Strategy:
 * 1) First unused color in palette.
 * 2) If all used, pick the color with the fewest occurrences (least used).
 */
export function pickNextColorForChart(chart: ChartConfig, palette: string[] = WaveChartDefaults.colors as unknown as string[]): string {
  const normalizedPalette = palette.map(p => normalizeColor(p)!)
  const usedColors = chart.curveReferences
    .map(ref => normalizeColor(ref.styling?.color))
    .filter((c): c is string => !!c)

  // Use a stable hash of the chart id to rotate the starting index so first-pick varies per chart,
  // and continue to honor that rotation when skipping used colors.
  const startIndex = hashString(chart.id) % normalizedPalette.length

  // 1) First unused color, starting from startIndex and wrapping around
  for (let step = 0; step < normalizedPalette.length; step++) {
    const i = (startIndex + step) % normalizedPalette.length
    const candidate = normalizedPalette[i]
    if (!usedColors.includes(candidate)) {
      return palette[i]
    }
  }

  // 2) Pick least-used color
  const counts = new Map<string, number>()
  normalizedPalette.forEach(c => counts.set(c, 0))
  usedColors.forEach(c => counts.set(c, (counts.get(c) || 0) + 1))

  let bestColor = normalizedPalette[0]
  let bestCount = counts.get(bestColor) ?? 0
  for (const c of normalizedPalette) {
    const cnt = counts.get(c) ?? 0
    if (cnt < bestCount) {
      bestColor = c
      bestCount = cnt
    }
  }

  // Return the original palette color string matching bestColor
  const idx = normalizedPalette.indexOf(bestColor)
  return idx >= 0 ? palette[idx] : palette[0]
}

// Simple stable hash for small strings (chart ids)
function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}
